import React, { useEffect, useRef } from "react";
import type { ProfileLinkConfig, SkillTag } from "../../../../../client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-regular-svg-icons";
import { cn } from "../../../../../components/utils/cn";
import EditorSkillTagButton from "../../../../../components/atoms/EditorSkillTagButton";
import { isString } from "../../../../../components";
import { DynamicInput } from "../../../../../components/input/DynamicInput";
import { AddSkillButton } from "../profile/AddSkillButton";
import { EditableSkillTag } from "../profile/EditableSkillTag";

interface Props {
  linkConfigData: ProfileLinkConfig;
  pdfMode?: boolean;
  className?: string;
  /** Render without the tinted rounded panel (used by minimal/flat templates). */
  flat?: boolean;
  /** When set, the intro (assignmentPitch) is inline-editable (commit on blur). */
  onEditIntro?: (value: string) => void;
  /** When set, each highlight bullet is inline-editable (commit on blur). Empty
   *  removes it (empty entries are filtered from the read view). */
  onEditHighlight?: (index: number, value: string) => void;
  /** When set, an "Add skill" button opens the skill picker (top skills). */
  onAddTopSkill?: () => void;
  /** When set, top-skill chips become inline-removable. */
  onRemoveTopSkill?: (name: string) => void;
}

export const ProfileSummaryContent = ({
  linkConfigData,
  pdfMode = false,
  className = "",
  flat = false,
  onEditIntro,
  onEditHighlight,
  onAddTopSkill,
  onRemoveTopSkill,
}: Props) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [linkConfigData.assignmentPitch]);

  return (
    <div
      className={cn(
        "flex flex-col items-center grow",
        flat ? "" : "p-5 rounded-2xl mx-2",
        flat
          ? ""
          : linkConfigData.profileSummaryConfig?.boxColor !== ""
            ? "bg-gray-1"
            : "",
      )}
    >
      <div className="w-full">
        {linkConfigData.profileSummaryConfig?.showIntroText &&
          (onEditIntro ? (
            <DynamicInput
              className="w-full text-sm whitespace-pre-wrap"
              inputClassName="text-sm"
              disabled={false}
              label="Add a custom motivation on why this profile is a good fit for this assignment"
              value={linkConfigData.assignmentPitch}
              validate={isString}
              rows={1}
              editable={true}
              onBlur={(
                event: React.FocusEvent<
                  HTMLInputElement | HTMLTextAreaElement
                >,
              ) => onEditIntro(event.target.value)}
              type="textarea"
            />
          ) : (
            <span className="w-full text-sm whitespace-pre-wrap">
              {linkConfigData.assignmentPitch}
            </span>
          ))}
        {linkConfigData.profileSummaryConfig?.showIntroText &&
          (linkConfigData.profileSummaryConfig.showHighlights ||
            linkConfigData.profileSummaryConfig.showTopSkills) && (
            <div className="bg-gray-3 w-full h-[1px] my-4" />
          )}
        {linkConfigData.profileSummaryConfig?.showHighlights &&
          linkConfigData.highlights &&
          (onEditHighlight
            ? linkConfigData.highlights.map((highlight, index) => (
                <div
                  key={index}
                  className={cn(
                    "flex items-center gap-2",
                    index === 0 ? "mt-0" : "mt-2",
                  )}
                >
                  <FontAwesomeIcon
                    icon={faCircleCheck}
                    size="sm"
                    color="#797C83"
                  />
                  <div className="flex-1">
                    <DynamicInput
                      inputClassName="text-sm text-black"
                      disabled={false}
                      label="Add a highlight or leave empty to remove"
                      value={highlight}
                      validate={isString}
                      rows={1}
                      editable={true}
                      onBlur={(
                        event: React.FocusEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >,
                      ) => onEditHighlight(index, event.target.value)}
                      type="textarea"
                    />
                  </div>
                </div>
              ))
            : linkConfigData.highlights
                .filter((h) => h !== "")
                .map((highlight, index) => (
                  <div
                    key={index}
                    className={cn(
                      "flex items-center gap-2",
                      index === 0 ? "mt-0" : "mt-2",
                    )}
                  >
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      size="sm"
                      color="#797C83"
                    />
                    <p
                      className={cn(
                        "text-sm text-black flex-1 ",
                        pdfMode ? "mb-4" : "",
                      )}
                    >
                      {highlight}
                    </p>
                  </div>
                )))}
      </div>

      {linkConfigData.profileSummaryConfig?.showTopSkills &&
        linkConfigData.profileSummaryConfig.showHighlights && (
          <div className="bg-gray-3 w-full h-[1px] my-4" />
        )}
      {linkConfigData.profileSummaryConfig?.showTopSkills && (
        <div className="flex flex-row flex-wrap items-center justify-start w-full gap-2">
          {onAddTopSkill && <AddSkillButton onClick={onAddTopSkill} />}
          {linkConfigData.profileSummaryConfig!.topSkills!.map(
            (skillTag, index) =>
              onRemoveTopSkill ? (
                <EditableSkillTag
                  key={skillTag.name}
                  skillTag={skillTag}
                  pdfMode={pdfMode}
                  onRemove={onRemoveTopSkill}
                />
              ) : (
                <EditorSkillTagButton
                  key={skillTag.name}
                  profileSkillTag={skillTag}
                  text={skillTag.name}
                  disableEditing={true}
                  handleClick={() => {}}
                  keepLeftPadding={true}
                  isStatic={true}
                  displayInfoOnHover={true}
                  pdfMode={pdfMode}
                />
              ),
          )}
        </div>
      )}
    </div>
  );
};
